// Centralized derivation for the standard set of projection milestones.
// Strategies call this from `computeMonth` so the rules stay uniform across
// products even though the call site is per-strategy. Product-specific
// milestones (e.g. shared-pool exhausted) can be appended by the strategy.
//
// All rules are evaluated against the current month's open state, close
// state, deltas, context, and the immediately prior month. No look-ahead.

import { carePhaseDefs } from '../../../CarePhaseDef';
import { careEnvironmentDefs } from '../../../CareEnvironmentDef';
import { getLtcPool } from './projectionTotals';

export interface DeriveStandardMilestonesInput<TState = unknown, TDeltas = unknown> {
  context: MonthContext;
  facts: MonthlyCareFacts;
  openState: MonthlyState<TState>;
  closeState: MonthlyState<TState>;
  deltas: MonthlyDeltas<TDeltas>;
  priorMonth: MonthlyEntry<TState, TDeltas> | null;
  // Total dollars paid by the policy this month (regular payout + any
  // retroactive lump in strategyDeltas). Used by the ep-lump milestone.
  // Strategies that have a retroactive lump pass it here; products without
  // one can pass 0.
  retroactiveLumpPaid?: number;
  // Floor used by the min-DB-reached milestone. Default 0, which still emits
  // when a positive death benefit crosses to zero.
  minimumGuaranteedDeathBenefit?: number;
  // Product strategies can disable the generic min-DB badge when the
  // `deathBenefit` pool represents a different concept, such as residual AV.
  emitMinimumDeathBenefitMilestone?: boolean;
  // When true, the client is already on LTC at the start of the projection,
  // so the `ltc-begins` milestone is suppressed on month 0 (it would otherwise
  // misleadingly label an in-progress claim as "LTC begins").
  clientHasStartedLtc?: boolean;
  // When true, the policy has at least one non-zero inflation rate
  // (rider, base pool, or EOB pool), so policy anniversaries actually
  // change something on the policy and the `anniversary` milestone is
  // worth surfacing. When false (flat policy), the anniversary milestone
  // is suppressed. Default false.
  hasInflationProtection?: boolean;
}

// Single source of truth for the `ltc-begins` label. Used both here (at
// derivation time, off the provisional age strategies seed) and by
// `processClaimProjection`, which re-labels this milestone once it has
// normalized `context.clientAge` to the authoritative birth-year basis.
// Keeping the wording in one place stops the two sites from drifting apart.
export function ltcBeginsLabel(clientAge: number | null | undefined): string {
  return clientAge != null ? `LTC begins at age ${clientAge}` : 'LTC begins';
}

function carePhaseLabel(value: CarePhase): string {
  return carePhaseDefs[value]?.label ?? String(value);
}

function careEnvironmentLabel(value: CareEnvironment): string {
  return careEnvironmentDefs[value]?.label ?? String(value);
}

export function deriveStandardMilestones<TState, TDeltas>(
  input: DeriveStandardMilestonesInput<TState, TDeltas>,
): ProjectionMilestone[] {
  const {
    context,
    openState,
    closeState,
    priorMonth,
    retroactiveLumpPaid = 0,
    minimumGuaranteedDeathBenefit = 0,
    emitMinimumDeathBenefitMilestone = true,
    clientHasStartedLtc = false,
    hasInflationProtection = false,
  } = input;
  const ms: ProjectionMilestone[] = [];

  // LTC begins: the first month of the projection marks the start of
  // projected LTC need (1-ADL threshold). Suppressed when the client is
  // already on LTC at projection start — the projection isn't telling us LTC
  // "begins" in that case, it's mid-claim.
  if (!priorMonth && !clientHasStartedLtc) {
    // Labeled off the provisional `context.clientAge`; `processClaimProjection`
    // re-labels this milestone after it normalizes the age to the birth-year
    // basis (see `ltcBeginsLabel` + the post-pass there).
    ms.push({ kind: 'ltc-begins', label: ltcBeginsLabel(context.clientAge) });
  }

  // Claim begins ("Payout begins") is NOT derived here. The 2-ADL gate clearing
  // does not mean money is flowing — the waiting period can still be running, so
  // the gate-clear month often pays $0. `processClaimProjection` emits the
  // `claim-start` milestone on each claimant's first POSITIVE-payout month in a
  // post-pass, so the milestone marks when benefits actually begin (and the
  // premium cutoff that keys off it stays correct).

  // EP retroactive lump sum paid.
  if (retroactiveLumpPaid > 0) {
    ms.push({ kind: 'ep-lump', label: 'WP Satisfied' });
  }

  // LTC pool fully drawn down this month.
  const openLtc = getLtcPool(openState);
  const closeLtc = getLtcPool(closeState);
  if (openLtc > 0 && closeLtc === 0) {
    ms.push({ kind: 'pool-exhausted', label: 'LTC pool exhausted' });
  }

  // Death benefit hit the guaranteed floor this month.
  if (
    emitMinimumDeathBenefitMilestone &&
    openState.pools.deathBenefit > minimumGuaranteedDeathBenefit &&
    closeState.pools.deathBenefit <= minimumGuaranteedDeathBenefit
  ) {
    ms.push({ kind: 'min-db-reached', label: 'Min death benefit' });
  }

  // Policy anniversary: the policy year ticked over since the prior month.
  // Only surfaced when the policy has some form of inflation protection —
  // on a flat policy the anniversary is a no-op tick worth nothing to advisors.
  if (hasInflationProtection && priorMonth && priorMonth.context.policyYear < context.policyYear) {
    ms.push({ kind: 'anniversary', label: 'Anniversary' });
  }

  // Phase / care-setting transitions vs. the prior month. When both change in
  // the same month (typical at the first care month), surface them as one
  // combined "<phase> · <environment>" badge rather than two adjacent chips —
  // the pair reads as a single care transition.
  const phaseChanged = priorMonth != null && priorMonth.context.carePhase !== context.carePhase;
  const envChanged =
    priorMonth != null && priorMonth.context.careEnvironment !== context.careEnvironment;
  if (phaseChanged && envChanged) {
    ms.push({
      kind: 'phase-change',
      label: `${carePhaseLabel(context.carePhase)} · ${careEnvironmentLabel(context.careEnvironment)}`,
    });
  } else if (phaseChanged) {
    ms.push({ kind: 'phase-change', label: carePhaseLabel(context.carePhase) });
  } else if (envChanged) {
    ms.push({ kind: 'env-change', label: careEnvironmentLabel(context.careEnvironment) });
  }

  return ms;
}
